import { Alert, Box } from '@palmetto/palmetto-components';
import { useFormikContext } from 'formik';
import { useCallback } from 'react';

import { isQCellsAcModule } from 'types';

interface ManufacturerWarningsProps {
  isAccountSpecific: boolean;
  fetchedResults: boolean;
}

interface Warning {
  variant: 'warning';
  message: string;
}

export const ManufacturerWarnings = ({ isAccountSpecific, fetchedResults }: ManufacturerWarningsProps) => {
  const { values } = useFormikContext<{
    panelManufacturer: string;
    panelModel: string;
  }>();

  const getManufacturerWarnings = useCallback((): Warning[] => {
    const warnings: Warning[] = [];
    const panelManufacturer = values?.panelManufacturer;
    const panelModel = values?.panelModel;

    if ((panelManufacturer || '').trim().startsWith('Mission') || panelManufacturer === 'SEG') {
      warnings.push({
        variant: 'warning' as const,
        message:
          'The panels selected require pre-approval from Palmetto LightReach. Please reach out to your RM to obtain approval before proceeding with this system design.',
      });
    }

    if (isQCellsAcModule(panelManufacturer, panelModel)) {
      warnings.push({
        variant: 'warning' as const,
        message:
          'QCells AC modules are not approved for accounts with a Contract Signed date after 9/21/2026. Please contact your RM with any questions.',
      });
    }

    if (isAccountSpecific && panelManufacturer) {
      const pfeManufacturers = ['Canadian Solar', 'JA', 'Jinko', 'Maxeon', 'Trina', 'VSun', 'Other'];

      if (pfeManufacturers.includes(panelManufacturer)) {
        warnings.push({
          variant: 'warning' as const,
          message:
            'The panels selected are from a Prohibited Foreign Entity (PFE) and therefore do not contribute any value towards FEOC eligibility. Please proceed with caution when designing systems with PFE equipment.',
        });
      }

      if (panelManufacturer === 'Longi') {
        warnings.push({
          variant: 'warning' as const,
          message:
            'Longi solar panels must be combined with Safe Harbored equipment to satisfy Palmetto LightReach FEOC requirements.',
        });
      }
    }

    return warnings;
  }, [values?.panelManufacturer, values?.panelModel, isAccountSpecific]);

  // Don't render if there's no data
  if (!fetchedResults) {
    return null;
  }

  return (
    <>
      {getManufacturerWarnings().map((warning, index) => (
        <Box key={`warning-${index}`} margin="sm 0">
          <Alert variant={warning.variant} hasIcon title={warning.message} />
        </Box>
      ))}
    </>
  );
};
